import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { BriefcaseBusinessIcon, ChevronsRight } from "lucide-react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ShowHeader } from "@/common/components/ShowHeader";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { useCompany } from "@/features/privateCompanies/hooks/useCompany";
import AccordionScrapRulesOverview from "../../../components/maintenanceScrapRules/AccordionOverview";
import AccordionScrapRulesEquipments from "../../../components/maintenanceScrapRules/AccordionEquipments";
import { CreateScrapRuleSchema, createScrapRuleSchema } from "../../../schemas/maintenanceScrapRules/createScrapRule.schema";
import AccordionScrapRulesExecutionRules from "../../../components/maintenanceScrapRules/AccordionExecutionRules";
import useScrapRule from "../../../hooks/maintenanceScrapRules/useScrapRule";
import useUpdateScrapRule from "../../../hooks/maintenanceScrapRules/useUpdateScrapRule";
import { ScrapRulesTransformer } from "@/features/privateCompanies/services/maintenanceScrapRules/scrapRulesTransformer";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

const Update = () => {
  const { companyId, id } = useParams();

  const goBack = useGoBack();
  const queryClient = useQueryClient()

  const { data: companyData } = useCompany(companyId || '');

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToUpdateMaintenanceScrapRules =
    isUserPermissionsPending || hasUserPermission('Admin::Maintenance::ScrapRules::Update')

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    mutate,
  } = useUpdateScrapRule({
    scrapRuleId: id,
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['scrapRule', id]})
      goBack({fallback: `/companies/${companyId}/maintenance/scrap-rules/${id}`})
    },
    onError: (error: any) => {
      if (error.response?.data.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError })(error)
      }
    }
  })

  const { data: scrapRule, isLoading: scrapRuleIsLoading } = useScrapRule(String(id))

  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors }
  } = useForm<CreateScrapRuleSchema>({
    resolver: zodResolver(createScrapRuleSchema)
  });

  const onSubmit: SubmitHandler<CreateScrapRuleSchema> = (data) => mutate(data);

  useEffect(() => {
    if (!scrapRuleIsLoading && scrapRule) {
      const formData = ScrapRulesTransformer.formDataTransformer(scrapRule)
      reset(formData)
    }
  }, [scrapRuleIsLoading])

  return (
    <form
      className="flex flex-col h-[calc(100vh-64px)]"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="false"
    >
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/companies/${companyId}/maintenance/scrap-rules` }}
        title={
          <div className="flex gap-2 items-center justify-start">
            <div className="flex gap-2 items-center">
              <ImageWithPlaceholder
                url={companyData?.general_information?.company_logo?.url}
                alt={companyData?.general_information?.fantasy_name}
                className="size-8"
                iconClassName="size-6"
                placeholderIcon={BriefcaseBusinessIcon}
              />
              <p className="text-white-400 font-normal text-base line-clamp-1">{companyData?.general_information?.fantasy_name}</p>
            </div>
            <ChevronsRight color='#ffffffb3' />
            <h2 className="text-xl font-bold text-ellipsis overflow-hidden whitespace-nowrap max-w-full line-clamp-1">Regras para desfazimento</h2>
          </div>
        }
        subtitle={scrapRule?.display_name}
        buttons={(
          <div className="flex items-center gap-4">
            <Button
              variant='outline-error'
              onClick={() => goBack({fallback: `/companies/${companyId}/maintenance/scrap-rules/${id}`})}
            >
              Cancelar
            </Button>
            {userHasPermissionToUpdateMaintenanceScrapRules && (
              <Button
                type="submit"
                name="submit"
              >
                Salvar
              </Button>
            )}
          </div>
        )}
      />

      <ErrorMessageWrapper statusCode={userHasPermissionToUpdateMaintenanceScrapRules ? 200 : 403}>
        <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <AccordionScrapRulesOverview />

          <TextField
            label="Nome da regra de desfazimento"
            markAsRequired
            errorMessage={errors?.display_name?.message}
            {...register('display_name')}
            maxLength={32}
          />

          <AccordionScrapRulesEquipments
            control={control}
            register={register}
            errors={errors}
          />

          <AccordionScrapRulesExecutionRules
            control={control}
            register={register}
            errors={errors}
          />
        </Box>

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError
          />
        )}
      </ErrorMessageWrapper>
    </form>
  );
};

export default Update;
